// src/adm/pages/UserDayLog.jsx
import { useEffect, useMemo, useState } from "react";
import AdmNav from "../components/AdmNav";
import { AdmBranchAPI, AdmLogAPI } from "../api";

/** 현재 월 (YYYY-MM) */
function getCurrentMonth() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** 해당 월의 일수 */
function getDaysInMonth(ym) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return 31;
  const [y, m] = ym.split("-").map((v) => Number(v));
  return new Date(y, m, 0).getDate();
}

/** 1~N → ["01","02",...] */
function rangeDaysLabels(n) {
  return Array.from({ length: n }, (_, i) => String(i + 1).padStart(2, "0"));
}

/** 지역 헤더가 있는 지점 셀렉트 */
function BranchSelect({ options, value, onChange }) {
  const grouped = useMemo(() => {
    const list = [];
    let currentArea = "";
    options.forEach((b) => {
      if (b.branch_area !== currentArea) {
        currentArea = b.branch_area;
        list.push({ type: "header", text: `-----${currentArea}-----` });
      }
      list.push({
        type: "item",
        value: String(b.branch_idx),
        text: b.branch_name,
      });
    });
    return list;
  }, [options]);

  return (
    <select id="branch" className="" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">지점선택</option>
      {grouped.map((opt, i) =>
        opt.type === "header" ? (
          <option key={`h-${i}`} value="" disabled>
            {opt.text}
          </option>
        ) : (
          <option key={opt.value} value={opt.value}>
            {opt.text}
          </option>
        )
      )}
    </select>
  );
}

export default function UserDayLog() {
  const [branches, setBranches] = useState([]);
  const [branchIdx, setBranchIdx] = useState("");
  const [month, setMonth] = useState(getCurrentMonth());

  /** 결과 */
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [rangeText, setRangeText] = useState("");

  /** UI 상태 */
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // 초기 접속 시 자동 조회 금지 — 지점 목록만 로드
  useEffect(() => {
    (async () => {
      try {
        const list = await AdmBranchAPI.list();
        setBranches(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setBranches([]);
      }
    })();
  }, []);

  function normalizeDayCounts(rowsInput, ym) {
    const dayCount = getDaysInMonth(ym);
    const labels = rangeDaysLabels(dayCount);
    const start = `${ym}-01`;
    const end = `${ym}-${String(dayCount).padStart(2, "0")}`;

    const map = new Map(); // id -> { '01': n, ... }

    (Array.isArray(rowsInput) ? rowsInput : []).forEach((r) => {
      const id = String(r.id ?? "");
      const dateStr = String(r.day ?? r.date ?? ""); // 서버는 day 사용 (예비로 date도 허용)
      if (!id || dateStr.length < 10) return;

      const dd = dateStr.slice(8, 10); // 'YYYY-MM-DD' → 'DD'
      if (!labels.includes(dd)) return;

      if (!map.has(id)) {
        map.set(
          id,
          labels.reduce((acc, d) => ((acc[d] = 0), acc), {})
        );
      }
      const c = Number(r.count ?? r.cnt ?? r.value ?? 0);
      map.get(id)[dd] += Number.isFinite(c) ? c : 0;
    });

    const rowsNorm = Array.from(map.entries()).map(([id, dayCounts]) => {
      const rowTotal = labels.reduce((s, d) => s + (dayCounts[d] || 0), 0);
      return { id, dayCounts, rowTotal };
    });

    const total = rowsNorm.reduce((s, r) => s + r.rowTotal, 0);
    return { labels, rows: rowsNorm, total, start, end };
  }

  /** 조회 버튼 */
  const onSearch = async () => {
    // ✅ 지점 미선택이면 전체 조회 허용 (월만 필수)
    if (!/^\d{4}-\d{2}$/.test(month)) {
      alert("조회월을 선택하세요.");
      return;
    }

    try {
      setLoading(true);
      setErr("");
      const resRows = await AdmLogAPI.dayCounts({
        // 지점 미선택 시 파라미터에서 제외되도록 undefined 전달
        branch_idx: branchIdx || undefined,
        year_month: month,
      });
      const { labels, rows, total, start, end } = normalizeDayCounts(resRows, month);

      const dayTotalsCalc = labels.map((d) => rows.reduce((s, r) => s + (r.dayCounts[d] || 0), 0));

      setHeaders(labels);
      setRows(rows.sort((a, b) => String(a.id).localeCompare(String(b.id))));
      setGrandTotal(total || dayTotalsCalc.reduce((s, v) => s + v, 0));
      setRangeText(`${start} ~ ${end} 총 사용량: ${total || dayTotalsCalc.reduce((s, v) => s + v, 0)}`);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "조회 실패");
      setHeaders([]);
      setRows([]);
      setGrandTotal(0);
      setRangeText("");
    } finally {
      setLoading(false);
    }
  };

  const dayTotals = useMemo(() => {
    if (!headers.length || !rows.length) return [];
    return headers.map((d) => rows.reduce((s, r) => s + (r.dayCounts?.[d] || 0), 0));
  }, [headers, rows]);

  // ⬇︎ month 인풋 전체 클릭으로 피커 열기 (사용자 제스처 컨텍스트)
  const handleMonthMouseDown = (e) => {
    const el = e.currentTarget;
    try {
      if (typeof el.showPicker === "function") el.showPicker(); // 사용자 제스처 컨텍스트
    } catch (_) {
      el.focus(); // 대체
    }
  };

  return (
    <>
      <AdmNav />
      <section className="adm-branch adm-UserDayLog">
        <div className="adm-card">
          <div className="adm-title-row">
            <h2>API 일별 사용량 조회</h2>
          </div>
          <div className="adm-toolbar">
            <div className="datafilterbox">
              <label className="">회사 :</label>
              <BranchSelect options={branches} value={branchIdx} onChange={setBranchIdx} />
              <label className="">조회월 :</label>
              <input
                type="month"
                className="adm-month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                onMouseDown={handleMonthMouseDown}
              />
            </div>
            <button className="btn" onClick={onSearch}>
              조회
            </button>
          </div>

          {/* 기간/총합 Summary */}
          {rangeText && <div className="total-data">{rangeText}</div>}

          <div className="adm-table-wrap">
            <table className="adm-table" cellPadding={0} cellSpacing={0}>
              <thead>
                <tr className="table-header">
                  <th>아이디</th>
                  {headers.map((d) => (
                    <th key={d}>{d}일</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="center" colSpan={Math.max(2, headers.length + 1)}>
                      로딩 중…
                    </td>
                  </tr>
                ) : err ? (
                  <tr>
                    <td className="center error" colSpan={Math.max(2, headers.length + 1)}>
                      {err}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="center" colSpan={Math.max(2, headers.length + 1)}>
                      결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="p-3">{r.id}</td>
                      {headers.map((hh) => {
                        const v = Number(r.dayCounts?.[hh] ?? 0);
                        return (
                          <td key={hh} className="p-3">
                            {v > 0 ? <span className="time-val">{v}</span> : `-`}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>

              {/* 총계 행 */}
              {headers.length > 0 && (
                <tfoot>
                  <tr className="table-footer">
                    <th>총계</th>
                    {dayTotals.map((n, i) => {
                      const v = Number(n || 0);
                      return <th key={i}>{v > 0 ? <span className="time-val">{v}</span> : `-`}</th>;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
